import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import styled, { keyframes, css } from 'styled-components';
import { FiCheckCircle, FiUsers, FiX, FiActivity, FiClock, FiAlertCircle } from 'react-icons/fi';
import {
  loadDoctorState,
  loadOpsState,
  changeDoctorStatus,
  doStartCall,
  doClaimCase,
  applyOpsUpdate,
} from '../../actions/aegonQueue';
import { setActiveConsult } from '../../actions';
import config from '../../config';
import {
  colors,
  space,
  radius,
  fontSize,
  fontWeight,
  lineHeight,
  fontFamily,
  shadow,
  focusRing,
  motion,
  z,
  withAlpha,
} from '../../styles/tokens';

// ────────────────────────────────────────────────────────────────────
// Panel-local theme. We pin the dark surface from tokens here so the
// rest of the component reads `t.bg` etc. without long property paths.
// ────────────────────────────────────────────────────────────────────
const t = {
  bg:           colors.dark.bg,
  surface:      colors.dark.surface,
  surfaceHi:    colors.dark.surfaceHi,
  border:       colors.dark.border,
  borderStrong: colors.dark.borderStrong,
  text:         colors.dark.text,
  textMuted:    colors.dark.textMuted,
  textDim:      colors.dark.textDim,
  primary:      colors.primary,
  primaryHi:    colors.primaryHi,
  success:      colors.success,
  successHi:    colors.successHi,
  warning:      colors.warning,
  warningHi:    colors.warningHi,
  danger:       colors.danger,
  dangerHi:     colors.dangerHi,
};

const STATUS_META = {
  AVAILABLE: { color: t.success,   label: 'Available' },
  IN_CALL:   { color: t.warning,   label: 'In Call'   },
  WRAP_UP:   { color: t.primary,   label: 'Wrap-Up'   },
  OFFLINE:   { color: t.textMuted, label: 'Offline'   },
};

// ────────────────────────────────────────────────────────────────────
// Animations — kept subtle; nothing bouncy.
// ────────────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.985); }
  to   { opacity: 1; transform: none; }
`;

const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// Slow, low-opacity ripple — gentler than the previous sharp pulse.
const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 ${withAlpha(t.success, 0.45)}; }
  100% { box-shadow: 0 0 0 14px ${withAlpha(t.success, 0)}; }
`;

// ────────────────────────────────────────────────────────────────────
// Reusable style fragments
// ────────────────────────────────────────────────────────────────────

// Visible only on keyboard nav — avoids ring noise on mouse clicks.
const focusRingMixin = (color = t.primary) => css`
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing(color, 0.4)};
  }
`;

const tabularNums = css`
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

// ────────────────────────────────────────────────────────────────────
// Layout chrome
// ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  /* Frosted-glass backdrop: blur + slight saturation lift for premium feel.
     Falls back to a flat dark tint where backdrop-filter is unsupported. */
  background: rgba(7, 9, 14, 0.55);
  -webkit-backdrop-filter: blur(8px) saturate(140%);
  backdrop-filter: blur(8px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${z.overlay};
  padding: ${space[4]};
  animation: ${overlayFade} ${motion.base};
`;

const Panel = styled.div`
  position: relative;
  width: 48rem;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${space[6]} ${space[6]} ${space[7]};
  background: ${t.bg};
  /* Hairline border on top of layered modal shadow. Together they read as
     a real lifted surface; either alone reads flat or harsh. */
  border: 1px solid ${t.border};
  border-radius: ${radius.xl};
  box-shadow: ${shadow.modal};
  color: ${t.text};
  font-family: ${fontFamily.sans};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};
  animation: ${fadeIn} ${motion.slow};

  /* A 1px highlight on the top edge — mimics a subtle reflected light source. */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: ${space[5]}; right: ${space[5]};
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${withAlpha(t.text, 0.08)} 50%,
      transparent
    );
    pointer-events: none;
  }

  /* Slim scrollbar styling — keep the modal looking clean on overflow. */
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${t.border};
    border-radius: ${radius.pill};
  }
  &::-webkit-scrollbar-thumb:hover { background: ${t.borderStrong}; }
`;

const PanelHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[4]};
  margin-bottom: ${space[5]};
  padding-bottom: ${space[4]};
  border-bottom: 1px solid ${t.border};
`;

const PanelTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  min-width: 0;
`;

/* Title is regular case, modern weight — uppercase + heavy letter-spacing
   reads dated. Eyebrow above gives context without shouting. */
const PanelEyebrow = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${t.primary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${fontSize['2xl']};
  font-weight: ${fontWeight.semibold};
  line-height: ${lineHeight.tight};
  letter-spacing: -0.01em;
  color: ${t.text};
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 3.2rem;
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
  color: ${t.textMuted};
  cursor: pointer;
  transition: color ${motion.fast}, border-color ${motion.fast}, background ${motion.fast};

  &:hover {
    color: ${t.text};
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }
  &:active { background: ${t.surfaceHi}; }
  ${focusRingMixin(t.primary)}
`;

// ────────────────────────────────────────────────────────────────────
// Status row
// ────────────────────────────────────────────────────────────────────

const StatusRow = styled.section`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  flex-wrap: wrap;
  margin-bottom: ${space[5]};
`;

/* Status as a single self-contained pill — clearer hierarchy than a loose
   dot + label combo. Pulses only when AVAILABLE. */
const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[1]} ${space[3]} ${space[1]} ${space[2]};
  background: ${(p) => withAlpha(p.color, 0.1)};
  border: 1px solid ${(p) => withAlpha(p.color, 0.28)};
  border-radius: ${radius.pill};
  color: ${(p) => p.color};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  letter-spacing: 0.01em;
`;

const StatusDot = styled.span`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: ${(p) => p.color};
  animation: ${(p) => (p.pulse ? ripple : 'none')} 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  flex-shrink: 0;
`;

const DoctorName = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.medium};
  color: ${t.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

/* Segmented control: a single rounded container with two cells.
   Reads more refined than two adjacent bordered buttons. */
const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${space[1]};
  background: ${t.surface};
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
  margin-left: auto;
`;

const SegmentBtn = styled.button`
  appearance: none;
  padding: ${space[1]} ${space[3]};
  background: ${(p) => (p.active ? t.surfaceHi : 'transparent')};
  border: 1px solid ${(p) => (p.active ? t.borderStrong : 'transparent')};
  border-radius: ${radius.sm};
  color: ${(p) => (p.active ? p.activeColor || t.text : t.textMuted)};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: color ${motion.fast}, background ${motion.fast}, border-color ${motion.fast};

  &:hover:not(:disabled) {
    color: ${t.text};
    background: ${t.surfaceHi};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  ${focusRingMixin(t.primary)}
`;

// ────────────────────────────────────────────────────────────────────
// Queue status card (for the available-but-empty / waiting summary)
// ────────────────────────────────────────────────────────────────────

const toneAccent = {
  calm:   t.success,
  warm:   t.warning,
  urgent: t.danger,
};

const QueueStatusCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[3]} ${space[4]};
  margin-bottom: ${space[4]};
  background: ${(p) => withAlpha(toneAccent[p.tone] || t.success, 0.06)};
  border: 1px solid ${(p) => withAlpha(toneAccent[p.tone] || t.border, 0.22)};
  border-radius: ${radius.lg};
  overflow: hidden;
  transition: background ${motion.base}, border-color ${motion.base};

  /* Vertical accent bar on the left edge — colored by tone.
     Subtler than tinting the whole card body. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${(p) => toneAccent[p.tone] || t.success};
    opacity: ${(p) => (p.tone === 'calm' ? 0.45 : 0.85)};
  }
`;

const QueueIconBox = styled.div`
  width: 3.6rem;
  height: 3.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => withAlpha(p.color || t.success, 0.14)};
  color: ${(p) => p.color || t.success};
  border-radius: ${radius.md};
  flex-shrink: 0;
`;

const QueueMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const QueueTitle = styled.div`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  line-height: ${lineHeight.tight};
  letter-spacing: -0.01em;
  color: ${(p) => p.color || t.text};
  ${tabularNums}

  span.suffix {
    margin-left: ${space[1]};
    font-size: ${fontSize.md};
    font-weight: ${fontWeight.medium};
    color: ${t.textMuted};
    letter-spacing: 0;
  }
`;

const QueueDetail = styled.div`
  font-size: ${fontSize.sm};
  color: ${t.textMuted};
  margin-top: ${space[1]};
  ${tabularNums}

  strong {
    color: ${(p) => p.timeColor || t.text};
    font-weight: ${fontWeight.semibold};
  }
`;

// ────────────────────────────────────────────────────────────────────
// Assigned case card
// ────────────────────────────────────────────────────────────────────

const CaseCard = styled.div`
  position: relative;
  padding: ${space[4]};
  margin-bottom: ${space[3]};
  background: ${t.surface};
  border: 1px solid ${t.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  overflow: hidden;

  /* Faint top accent — primary-tinted, communicates "this is yours". */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${withAlpha(t.primary, 0.6)} 50%,
      transparent
    );
  }
`;

const CaseName = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  line-height: ${lineHeight.tight};
  letter-spacing: -0.01em;
  margin-bottom: ${space[1]};
  color: ${t.text};
`;

const CaseMeta = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${space[2]} ${space[5]};
  margin: ${space[3]} 0 0;
`;

const MetaCell = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.dt`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  color: ${t.textDim};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: ${space[1]};
`;

const MetaValue = styled.dd`
  margin: 0;
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  ${tabularNums}
  word-break: break-word;
`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  margin-top: ${space[2]};
  padding: ${space[1]} ${space[2]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${t.warning};
  background: ${withAlpha(t.warning, 0.12)};
  border: 1px solid ${withAlpha(t.warning, 0.25)};
  border-radius: ${radius.sm};
  ${tabularNums}
`;

// ────────────────────────────────────────────────────────────────────
// Primary CTA
// ────────────────────────────────────────────────────────────────────

const StartBtn = styled.button`
  width: 100%;
  margin-top: ${space[3]};
  padding: ${space[3]} ${space[4]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  /* Subtle vertical gradient adds depth without crossing into skeuomorphism. */
  background: linear-gradient(180deg, ${t.successHi}, ${t.success});
  color: #04140a;
  border: 1px solid ${withAlpha('#000', 0.15)};
  border-radius: ${radius.md};
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow:
    0 1px 0 ${withAlpha('#fff', 0.18)} inset,
    ${shadow.sm};
  transition: transform ${motion.fast}, box-shadow ${motion.fast}, filter ${motion.fast};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 1px 0 ${withAlpha('#fff', 0.22)} inset,
      0 6px 16px -6px ${withAlpha(t.success, 0.55)};
  }
  &:active:not(:disabled) { transform: translateY(0); filter: brightness(0.96); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  ${focusRingMixin(t.success)}
`;

// Inline three-dot loader for the connecting state — replaces the bare
// "Connecting…" text and reads as a proper loading affordance.
const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 0.3rem;

  span {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotPulse} 1.2s infinite ease-in-out both;
  }
  span:nth-child(2) { animation-delay: 0.16s; }
  span:nth-child(3) { animation-delay: 0.32s; }
`;

// ────────────────────────────────────────────────────────────────────
// Idle / empty state
// ────────────────────────────────────────────────────────────────────

const IdleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${space[7]} ${space[4]} ${space[6]};
  gap: ${space[2]};
`;

const IdleIconWrap = styled.div`
  width: 5.6rem;
  height: 5.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.lg};
  background: ${withAlpha(t.primary, 0.1)};
  border: 1px solid ${withAlpha(t.primary, 0.22)};
  color: ${t.primary};
  margin-bottom: ${space[2]};
`;

const IdleTitle = styled.div`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  letter-spacing: -0.005em;
`;

const IdleText = styled.div`
  font-size: ${fontSize.base};
  color: ${t.textMuted};
  max-width: 32rem;
  line-height: ${lineHeight.normal};
`;

// ────────────────────────────────────────────────────────────────────
// Waiting list
// ────────────────────────────────────────────────────────────────────

const WaitingListWrap = styled.section`
  margin-bottom: ${space[3]};
`;

const WaitingListLabel = styled.h3`
  margin: 0 0 ${space[2]};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${t.textDim};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const WaitingItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[3]};
  background: ${t.surface};
  /* Tone the border by urgency rather than tinting the whole row —
     less visual noise across many rows. */
  border: 1px solid ${(p) =>
    p.urgent  ? withAlpha(t.danger,  0.4) :
    p.warning ? withAlpha(t.warning, 0.32) :
                t.border};
  border-radius: ${radius.md};
  margin-bottom: ${space[2]};
  transition: background ${motion.fast}, border-color ${motion.fast}, transform ${motion.fast};

  &:last-child { margin-bottom: 0; }
  &:hover { background: ${t.surfaceHi}; }
`;

/* Avatar gradient — subtle two-stop gradient reads richer than a flat tint. */
const WaitingAvatar = styled.div`
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${withAlpha(t.primary, 0.28)},
    ${withAlpha(t.primary, 0.12)}
  );
  border: 1px solid ${withAlpha(t.primary, 0.4)};
  color: ${t.primaryHi};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.02em;
  flex-shrink: 0;
`;

const WaitingInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const WaitingName = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WaitingMeta = styled.div`
  margin-top: 0.2rem;
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  color: ${t.textMuted};
  letter-spacing: 0.02em;
  ${tabularNums}
`;

const WaitingTime = styled.div`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.bold};
  color: ${(p) =>
    p.urgent  ? t.danger  :
    p.warning ? t.warning :
                t.success};
  flex-shrink: 0;
  ${tabularNums}
`;

const TakeBtn = styled.button`
  flex-shrink: 0;
  padding: ${space[1]} ${space[3]};
  background: ${withAlpha(t.success, 0.14)};
  color: ${t.successHi};
  border: 1px solid ${withAlpha(t.success, 0.35)};
  border-radius: ${radius.sm};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: background ${motion.fast}, color ${motion.fast}, border-color ${motion.fast}, transform ${motion.fast};

  &:hover:not(:disabled) {
    background: ${t.success};
    color: #04140a;
    border-color: ${t.success};
    transform: translateY(-1px);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  ${focusRingMixin(t.success)}
`;

const OverflowNote = styled.div`
  text-align: center;
  font-size: ${fontSize.xs};
  color: ${t.textDim};
  padding: ${space[2]} ${space[1]} 0;
  ${tabularNums}
`;

// ────────────────────────────────────────────────────────────────────
// New-case toast (currently rendered behind a `false &&` flag in the
// existing logic — kept for parity in case it's re-enabled).
// ────────────────────────────────────────────────────────────────────

const NewCaseToast = styled.div`
  position: absolute;
  top: ${space[3]};
  left: 50%;
  transform: translateX(-50%);
  min-width: 26rem;
  padding: ${space[3]} ${space[4]};
  background: ${t.surface};
  border: 1px solid ${withAlpha(t.primary, 0.5)};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  animation: ${fadeIn} ${motion.base};
  z-index: ${z.raised};
`;

const ToastTitle = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  color: ${t.primaryHi};
  margin-bottom: ${space[1]};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ToastRow = styled.div`
  font-size: ${fontSize.sm};
  color: ${t.textMuted};
  ${tabularNums}

  span { color: ${t.text}; font-weight: ${fontWeight.semibold}; }
`;

// ────────────────────────────────────────────────────────────────────
// Errors
// ────────────────────────────────────────────────────────────────────

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space[1]};
  margin-top: ${space[2]};
  padding: ${space[2]} ${space[3]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  color: ${t.dangerHi};
  background: ${withAlpha(t.danger, 0.08)};
  border: 1px solid ${withAlpha(t.danger, 0.25)};
  border-radius: ${radius.sm};
`;

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const fmtSeconds = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────

function AegonInstantPanel(props) {
  const {
    doctorId, doctorFirstName, doctorLastName,
    doctorStatus, assignedCase, queue, onClose,
  } = props;

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [toast, setToast] = useState(null);
  const [, setTick] = useState(0);
  const [claimingId, setClaimingId] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const pubnubRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    props.loadDoctorState();
    props.loadOpsState();
    const tickId = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(tickId);
    // mapDispatchToProps uses the function form, so prop-bound dispatchers
    // get a fresh ref every render — depending on them here would re-fire
    // mount logic on every render. Mount-once is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    setupPubNub(doctorId);
    return () => {
      if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    };
    // setupPubNub is defined inline below; only re-run on doctorId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  // ESC closes the modal — standard expectation for any dialog. Pure a11y
  // win, no functional change to the data flow.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setupPubNub = (id) => {
    if (pubnubRef.current) pubnubRef.current.unsubscribeAll();
    const pn = new PubNub({
      publishKey: config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid: `doctor-panel-${id}`,
    });
    pn.addListener({
      message: ({ message }) => {
        if (message.name === 'new_case_inbound') {
          props.loadDoctorState();
          showToast({
            customerName: message.customerName,
            proposalNumber: message.proposalNumber,
            waitSeconds: message.waitSeconds,
          });
        }
        if (message.name === 'ops_queue_update') {
          props.applyOpsUpdate(message);
        }
      },
    });
    const ns = props.insurer || 'aegon';
    pn.subscribe({ channels: [`${ns}-instant-doctor-${id}`, `${ns}-instant-ops`] });
    pubnubRef.current = pn;
  };

  const showToast = (data) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(data);
    toastTimerRef.current = setTimeout(() => setToast(null), 7000);
  };

  const handleClaim = async (caseRow) => {
    if (claimingId) return;
    setClaimingId(caseRow.consultationId);
    setClaimError(null);
    try {
      const result = await props.dispatch(doClaimCase(caseRow.consultationId));
      if (!result) {
        setClaimError('Something went wrong. Please try again.');
        setClaimingId(null);
        return;
      }
      if (result.status === 'already_taken') {
        setClaimError('That case was just taken by another doctor.');
        setClaimingId(null);
        return;
      }
      if (result.status === 'success') {
        props.dispatch(setActiveConsult({
          consultationId: caseRow.consultationId,
          callId: result.callId,
          user: {
            userId: caseRow.userId || result.userId || null,
            userName: caseRow.name,
            gender: caseRow.gender || '',
          },
          labels: [],
          oneConnectLinks: [],
          relatives: [],
          requestType: 'video',
          callType: 'video',
          channel: `aegon-instant-${caseRow.consultationId}`,
        }));
        props.dispatch({
          type: 'INITIATE_VIDEO_CALL',
          payload: {
            isInsta: 1,
            instantConsultationId: caseRow.consultationId,
          },
        });
        onClose();
      }
    } catch (err) {
      console.error('[AegonInstantPanel handleClaim]', err);
      setClaimError('Something went wrong. Please try again.');
      setClaimingId(null);
    }
  };

  const handleStartCall = async () => {
    if (!assignedCase || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await props.doStartCall(assignedCase.consultation_id);
      if (!result || result.status !== 'success') {
        setStartError(result && result.errorMessage ? result.errorMessage : 'Failed to start call');
        setStarting(false);
        return;
      }
      props.dispatch(setActiveConsult({
        consultationId: assignedCase.consultation_id,
        callId: result.callId,
        user: {
          userId: assignedCase.user_id,
          userName: assignedCase.name,
          gender: assignedCase.gender || '',
        },
        labels: [],
        oneConnectLinks: [],
        relatives: [],
        requestType: 'video',
        callType: 'video',
        channel: `aegon-instant-${assignedCase.consultation_id}`,
      }));
      props.dispatch({
        type: 'INITIATE_VIDEO_CALL',
        payload: {
          isInsta: 1,
          instantConsultationId: assignedCase.consultation_id,
        },
      });
      onClose();
    } catch (err) {
      console.error('[AegonInstantPanel handleStartCall]', err);
      setStartError('Something went wrong. Please try again.');
    }
    setStarting(false);
  };

  const status = doctorStatus ? doctorStatus.status : 'OFFLINE';
  const statusMeta = STATUS_META[status] || STATUS_META.OFFLINE;

  const waitingCases = (queue || []).filter((c) => c.status === 'WAITING');
  const waitingCount = waitingCases.length;
  const longestWait = waitingCases.reduce((max, c) => {
    const elapsed = c.queuedAt
      ? Math.floor((Date.now() - new Date(c.queuedAt).getTime()) / 1000)
      : (c.elapsedSeconds || 0);
    return Math.max(max, elapsed);
  }, 0);
  const queueTone = waitingCount === 0 ? 'calm'
                  : longestWait > 180  ? 'urgent'
                                       : 'warm';
  const longestWaitColor = longestWait > 180 ? t.danger
                         : longestWait > 90  ? t.warning
                                             : t.text;

  return (
    <Overlay
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby="aegon-instant-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        { false && toast && (
          <NewCaseToast role="status" aria-live="polite">
            <ToastTitle>New Case Assigned</ToastTitle>
            <ToastRow>Customer: <span>{toast.customerName}</span></ToastRow>
            <ToastRow>App ID: <span>{toast.proposalNumber}</span></ToastRow>
            {toast.waitSeconds > 0 && (
              <ToastRow>Waited: <span>{fmtSeconds(toast.waitSeconds)}</span></ToastRow>
            )}
          </NewCaseToast>
        )}

        <PanelHeader>
          <PanelTitleGroup>
            <PanelEyebrow>Aegon · Instant</PanelEyebrow>
            <PanelTitle id="aegon-instant-panel-title">Instant vMER Queue</PanelTitle>
          </PanelTitleGroup>
          <CloseBtn type="button" onClick={onClose} aria-label="Close panel">
            <FiX size={16} aria-hidden="true" />
          </CloseBtn>
        </PanelHeader>

        <StatusRow aria-live="polite">
          <StatusPill color={statusMeta.color}>
            <StatusDot color={statusMeta.color} pulse={status === 'AVAILABLE'} />
            {statusMeta.label}
          </StatusPill>
          <DoctorName>Dr. {doctorFirstName} {doctorLastName}</DoctorName>
          <Segmented role="group" aria-label="Doctor availability">
            <SegmentBtn
              type="button"
              active={status === 'AVAILABLE'}
              activeColor={t.success}
              aria-pressed={status === 'AVAILABLE'}
              onClick={() => props.changeDoctorStatus('AVAILABLE')}
              disabled={status === 'IN_CALL'}
            >
              Available
            </SegmentBtn>
            <SegmentBtn
              type="button"
              active={status === 'OFFLINE'}
              activeColor={t.text}
              aria-pressed={status === 'OFFLINE'}
              onClick={() => props.changeDoctorStatus('OFFLINE')}
              disabled={status === 'IN_CALL'}
            >
              Offline
            </SegmentBtn>
          </Segmented>
        </StatusRow>

        {status !== 'OFFLINE' && (
          <QueueStatusCard tone={queueTone}>
            {waitingCount === 0 ? (
              <>
                <QueueIconBox color={t.success}>
                  <FiCheckCircle size={18} aria-hidden="true" />
                </QueueIconBox>
                <QueueMain>
                  <QueueTitle color={t.success}>Queue is clear</QueueTitle>
                  <QueueDetail>No customers waiting right now</QueueDetail>
                </QueueMain>
              </>
            ) : (
              <>
                <QueueIconBox color={queueTone === 'urgent' ? t.danger : t.warning}>
                  <FiUsers size={18} aria-hidden="true" />
                </QueueIconBox>
                <QueueMain>
                  <QueueTitle color={queueTone === 'urgent' ? t.danger : t.warning}>
                    {waitingCount}
                    <span className="suffix">
                      customer{waitingCount > 1 ? 's' : ''} waiting
                    </span>
                  </QueueTitle>
                  {longestWait > 0 && (
                    <QueueDetail timeColor={longestWaitColor}>
                      Longest wait: <strong>{fmtSeconds(longestWait)}</strong>
                    </QueueDetail>
                  )}
                </QueueMain>
              </>
            )}
          </QueueStatusCard>
        )}

        {assignedCase ? (
          <>
            <CaseCard>
              <CaseName>{assignedCase.name}</CaseName>
              {(assignedCase.waitSeconds || assignedCase.wait_seconds) > 0 && (
                <WaitBadge>
                  <FiClock size={12} aria-hidden="true" />
                  Waited {fmtSeconds(assignedCase.waitSeconds || assignedCase.wait_seconds)}
                </WaitBadge>
              )}
              <CaseMeta>
                <MetaCell>
                  <MetaLabel>Proposal Number</MetaLabel>
                  <MetaValue>{assignedCase.proposalNumber || '—'}</MetaValue>
                </MetaCell>
                <MetaCell>
                  <MetaLabel>Gender</MetaLabel>
                  <MetaValue>{assignedCase.gender || '—'}</MetaValue>
                </MetaCell>
                <MetaCell>
                  <MetaLabel>Phone</MetaLabel>
                  <MetaValue>{assignedCase.phone || '—'}</MetaValue>
                </MetaCell>
                <MetaCell>
                  <MetaLabel>Status</MetaLabel>
                  <MetaValue>{assignedCase.status}</MetaValue>
                </MetaCell>
              </CaseMeta>
            </CaseCard>
            <StartBtn type="button" onClick={handleStartCall} disabled={starting}>
              {starting ? (
                <>
                  Connecting
                  <Dots aria-hidden="true"><span /><span /><span /></Dots>
                </>
              ) : 'Start Call Now'}
            </StartBtn>
            {startError && (
              <ErrorText role="alert">
                <FiAlertCircle size={14} aria-hidden="true" />
                {startError}
              </ErrorText>
            )}
          </>
        ) : status === 'AVAILABLE' && waitingCount > 0 ? (
          <WaitingListWrap>
            <WaitingListLabel>Available to take</WaitingListLabel>
            {waitingCases.slice(0, 5).map((c) => {
              const elapsed = c.queuedAt
                ? Math.floor((Date.now() - new Date(c.queuedAt).getTime()) / 1000)
                : (c.elapsedSeconds || 0);
              const urgent  = elapsed > 180;
              const warning = elapsed > 90;
              const isClaimingThis = claimingId === c.consultationId;
              return (
                <WaitingItem key={c.consultationId} urgent={urgent} warning={warning}>
                  <WaitingAvatar aria-hidden="true">
                    {(c.name || '?')[0].toUpperCase()}
                  </WaitingAvatar>
                  <WaitingInfo>
                    <WaitingName>{c.name}</WaitingName>
                    <WaitingMeta>
                      {c.proposalNumber || '—'}{c.gender ? ` · ${c.gender}` : ''}
                    </WaitingMeta>
                  </WaitingInfo>
                  <WaitingTime urgent={urgent} warning={warning}>
                    {fmtSeconds(elapsed)}
                  </WaitingTime>
                  <TakeBtn
                    type="button"
                    onClick={() => handleClaim(c)}
                    disabled={!!claimingId}
                  >
                    {isClaimingThis ? 'Taking…' : 'Take'}
                  </TakeBtn>
                </WaitingItem>
              );
            })}
            {waitingCount > 5 && (
              <OverflowNote>+{waitingCount - 5} more in queue</OverflowNote>
            )}
            {claimError && (
              <ErrorText role="alert">
                <FiAlertCircle size={14} aria-hidden="true" />
                {claimError}
              </ErrorText>
            )}
          </WaitingListWrap>
        ) : (
          <IdleBox>
            <IdleIconWrap>
              <FiActivity size={26} aria-hidden="true" />
            </IdleIconWrap>
            {status === 'OFFLINE' ? (
              <>
                <IdleTitle>You're offline</IdleTitle>
                <IdleText>Switch to <strong>Available</strong> to start receiving cases.</IdleText>
              </>
            ) : status === 'WRAP_UP' ? (
              <>
                <IdleTitle>Wrap-up in progress</IdleTitle>
                <IdleText>You'll be auto-set to Available shortly.</IdleText>
              </>
            ) : (
              <>
                <IdleTitle>You're available</IdleTitle>
                <IdleText>Waiting for the next case — new cases will appear here automatically.</IdleText>
              </>
            )}
          </IdleBox>
        )}
      </Panel>
    </Overlay>
  );
}

const mapStateToProps = (state) => ({
  doctorId:        state.user.doctorId,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
  insurer:         state.user.insurer,
  doctorStatus:    state.aegonQueue.doctorStatus,
  assignedCase:    state.aegonQueue.assignedCase,
  queue:           state.aegonQueue.queue,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  loadDoctorState:    (...args) => dispatch(loadDoctorState(...args)),
  loadOpsState:       (...args) => dispatch(loadOpsState(...args)),
  changeDoctorStatus: (...args) => dispatch(changeDoctorStatus(...args)),
  doStartCall:        (...args) => dispatch(doStartCall(...args)),
  applyOpsUpdate:     (...args) => dispatch(applyOpsUpdate(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AegonInstantPanel);
